import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Me } from "../../shared/types.ts";
import { api } from "./api.ts";
import { useI18n } from "./i18n.tsx";

type Ctx = {
  me: Me | null;
  loading: boolean;
  setMe: (me: Me | null) => void;
  refresh: () => Promise<Me | null>;
  logout: () => Promise<void>;
};

const MeContext = createContext<Ctx | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const [me, setMeState] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const { setLocale } = useI18n();

  const setMe = useCallback(
    (m: Me | null) => {
      setMeState(m);
      if (m && !m.needs_onboarding) setLocale(m.locale);
    },
    [setLocale],
  );

  const refresh = useCallback(async () => {
    try {
      const m = await api<Me>("/api/me");
      setMe(m);
      return m;
    } catch {
      setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [setMe]);

  const logout = useCallback(async () => {
    await api("/api/auth/logout", { body: {} }).catch(() => undefined);
    setMe(null);
  }, [setMe]);

  useEffect(() => {
    // Boot fetch; deferred a tick so the effect itself never sets state synchronously.
    let cancelled = false;
    void Promise.resolve().then(() => (cancelled ? undefined : refresh()));
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const value = useMemo<Ctx>(
    () => ({ me, loading, setMe, refresh, logout }),
    [me, loading, setMe, refresh, logout],
  );
  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): Ctx {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error("useMe outside MeProvider");
  return ctx;
}

export const INVITE_STORAGE_KEY = "yabe.invite";

export function storedInvite(): string | null {
  try {
    return localStorage.getItem(INVITE_STORAGE_KEY);
  } catch {
    return null;
  }
}
